import {
    createPagePagingActions,
    Field,
    FinalFormInput,
    FinalFormSwitch,
    SortDirection,
    StackSwitchApiContext,
    Table as AdminTable,
    TableFilterFinalForm,
    TableQuery,
    useTableQuery,
    useTableQueryFilter,
    useTableQueryPaging,
    useTableQuerySort,
    VisibleType,
} from "@comet/admin";
import { Restore } from "@mui/icons-material";
import { FormControlLabel, Grid, IconButton } from "@mui/material";
import { ContentWrapper, formatDateTimeAsString, FormattedDateWidthDateTime, useMemoizedTableVariables } from "@src/components";
import { StaticSelectField } from "@src/components/fields/StaticSelect";
import { ResetButton } from "@src/components/ResetButton";
import CountrySelect from "@src/components/select/CountrySelect";
import { StyledFilter, StyledSearch, TableFilter } from "@src/components/table/TableStyles";
import { type GQLCamera, GQLCameraStatus, type GQLQueryusersListAdminArgs, type GQLUsersListAdminQuery } from "@src/graphql.generated";
import ArrowRight from "@src/icons/ArrowRight";
import { type ExportVariables, loadLimit } from "@src/utils";
import { CSVExportButton } from "@src/utils/CSVExportButton";
import { useCSVExportPagedTableQuery } from "@src/utils/usePagedCSVExport";
import { type FunctionComponent, type PropsWithChildren, useCallback, useContext, useState } from "react";
import { useIntl } from "react-intl";

import { RestoreUserDialog } from "./RestoreUserDialog";
import { usersListQuery } from "./Table.gql";
import { SwitchContainer } from "./Table.sc";

interface TableProps {
    persistedStateId?: string;
}

const Table: FunctionComponent<PropsWithChildren<TableProps>> = ({ persistedStateId }) => {
    const pagingApi = useTableQueryPaging(1, { persistedStateId });
    const sortApi = useTableQuerySort({ columnName: "user.firstname", direction: SortDirection.DESC }, { persistedStateId });
    const filterApi = useTableQueryFilter<GQLQueryusersListAdminArgs>({}, { pagingApi, persistedStateId });
    const stackApi = useContext(StackSwitchApiContext);

    const [userId, setUserId] = useState<string | null>(null);

    const intl = useIntl();
    const { cameraStatus, country, query, withDeleted } = filterApi.current;
    const addAdditionalQueryVariablesFn = useCallback(
        () => ({ cameraStatus, country, query, withDeleted }),
        [cameraStatus, country, query, withDeleted],
    );

    const memoizedVariables = useMemoizedTableVariables<GQLQueryusersListAdminArgs>({ pagingApi, sortApi, addAdditionalQueryVariablesFn });

    const { tableData, api, loading, error } = useTableQuery<GQLUsersListAdminQuery, GQLQueryusersListAdminArgs>()(usersListQuery, {
        resolveTableData: ({ usersListAdmin }) => ({
            data: usersListAdmin.nodes,
            totalCount: usersListAdmin.totalCount,
            pagingInfo: createPagePagingActions(pagingApi, usersListAdmin),
        }),
        variables: memoizedVariables,
    });

    const cameraStatusOptions = [
        {
            label: intl.formatMessage({ id: "reviermanager.pages.selectOptionActivated", defaultMessage: "Mit Lizenz" }),
            value: GQLCameraStatus.Active,
        },
        {
            label: intl.formatMessage({ id: "reviermanager.pages.selectOptionNotActivated", defaultMessage: "Ohne Lizenz" }),
            value: GQLCameraStatus.Sleep,
        },
    ];

    const handleFilterReset = () => {
        filterApi.formApi.reset({});
    };

    const fileName = `${intl.formatMessage({
        id: "reviermanager.common.export.users",
        defaultMessage: "Kunden",
    })}`;

    const exportApi = useCSVExportPagedTableQuery<ExportVariables>(
        api,
        {
            fromPage: 1,
            toPage: tableData?.totalCount ? Math.ceil(tableData.totalCount / loadLimit) : 1,
            variablesForPage: (page) => {
                return {
                    ...memoizedVariables,
                    page,
                    size: loadLimit,
                };
            },
        },
        fileName,
    );

    return (
        <ContentWrapper marginVariant="table">
            <RestoreUserDialog id={userId} handleClose={() => setUserId(null)} />
            <TableFilterFinalForm filterApi={filterApi}>
                <TableFilter container>
                    <Grid size={1}>
                        <StyledFilter>
                            <StaticSelectField
                                name="country"
                                label={intl.formatMessage({ id: "reviermanager.user.country", defaultMessage: "Land" })}
                                component={CountrySelect}
                                placeholder={intl.formatMessage({
                                    id: "reviermanager.filter.all",
                                    defaultMessage: "Alle",
                                })}
                                isClearable
                                variant="vertical"
                            />
                        </StyledFilter>
                    </Grid>
                    <Grid size={2}>
                        <StyledFilter>
                            <StaticSelectField
                                name="cameraStatus"
                                label={intl.formatMessage({
                                    id: "reviermanager.pages.usersFilterLabelLicense",
                                    defaultMessage: "Lizenz",
                                })}
                                options={cameraStatusOptions}
                                isClearable
                                variant="vertical"
                                placeholder={intl.formatMessage({
                                    id: "reviermanager.filter.all",
                                    defaultMessage: "Alle",
                                })}
                            />
                        </StyledFilter>
                    </Grid>
                    <Grid size={3}>
                        <StyledFilter>
                            <Field
                                type="text"
                                label={intl.formatMessage({ id: "reviermanager.pages.usersFilterLabelQuery", defaultMessage: "Suche" })}
                                name="query"
                                component={FinalFormInput}
                                placeholder={intl.formatMessage({
                                    id: "reviermanager.pages.usersFilter",
                                    defaultMessage: "Name oder E-Mail",
                                })}
                                startAdornment={<StyledSearch />}
                                variant="vertical"
                                fullWidth
                            />
                        </StyledFilter>
                    </Grid>
                    <Grid size={2}>
                        <SwitchContainer>
                            <Field name="withDeleted" type="checkbox">
                                {(props) => (
                                    <FormControlLabel
                                        label={intl.formatMessage({
                                            id: "reviermanager.user.filter.withDeleted",
                                            defaultMessage: "Gelöschte anzeigen",
                                        })}
                                        control={<FinalFormSwitch {...props} />}
                                    />
                                )}
                            </Field>
                        </SwitchContainer>
                    </Grid>
                    {Object.keys(filterApi.current).length > 0 && (
                        <Grid>
                            <ResetButton onClick={handleFilterReset} />
                        </Grid>
                    )}
                </TableFilter>
            </TableFilterFinalForm>
            <TableQuery api={api} loading={loading} error={error}>
                {tableData && (
                    <AdminTable
                        exportApis={[exportApi]}
                        {...tableData}
                        sortApi={sortApi}
                        rowName={intl.formatMessage(
                            {
                                id: "reviermanager.pages.users",
                                defaultMessage: "{count, plural, one {Kunde} other {Kunden}}",
                            },
                            { count: tableData.totalCount },
                        )}
                        columns={[
                            {
                                name: "user.firstname",
                                header: intl.formatMessage({ id: "reviermanager.common.firstname", defaultMessage: "Vorname" }),
                                cellProps: { size: "medium" },
                                sortable: true,
                                render: ({ firstname }) => firstname,
                            },
                            {
                                name: "user.lastname",
                                header: intl.formatMessage({ id: "reviermanager.common.lastname", defaultMessage: "Nachname" }),
                                cellProps: { size: "medium" },
                                sortable: true,
                                render: ({ lastname }) => lastname,
                            },
                            {
                                name: "user.email",
                                header: intl.formatMessage({ id: "reviermanager.common.email", defaultMessage: "E-Mail" }),
                                cellProps: { size: "medium" },
                                sortable: true,
                                render: ({ email }) => email,
                            },
                            {
                                name: "cameras",
                                header: intl.formatMessage({
                                    id: "reviermanager.common.cameras",
                                    defaultMessage: "Kameras ges./mit Lizenz",
                                }),
                                cellProps: { size: "medium" },
                                render: (row) =>
                                    `${row.cameras.length}/${
                                        row.cameras.filter((camera: GQLCamera) => camera.status === GQLCameraStatus.Active).length
                                    }`,
                            },
                            {
                                name: "user.createdAt",
                                header: intl.formatMessage({ id: "reviermanager.common.creatAt", defaultMessage: "erstellt am" }),
                                cellProps: { size: "medium" },
                                sortable: true,
                                render: ({ createdAt }) => <FormattedDateWidthDateTime value={createdAt} />,
                                renderExcel: ({ createdAt }) => formatDateTimeAsString(createdAt, intl),
                            },
                            {
                                name: "user.lastValidated",
                                header: intl.formatMessage({ id: "reviermanager.common.lastValidated", defaultMessage: "validiert am" }),
                                cellProps: { size: "medium" },
                                sortable: true,
                                render: ({ lastValidated }) => {
                                    if (lastValidated) {
                                        return <FormattedDateWidthDateTime value={lastValidated} />;
                                    }
                                },
                                renderExcel: ({ lastValidated }) => formatDateTimeAsString(lastValidated, intl),
                            },
                            {
                                name: "actions",
                                visible: { [VisibleType.Browser]: true, [VisibleType.Export]: false },
                                render: ({ id }) => {
                                    if (withDeleted) {
                                        return (
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setUserId(id);
                                                }}
                                            >
                                                <Restore color="primary" />
                                            </IconButton>
                                        );
                                    }

                                    return (
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                stackApi.activatePage("edit", String(id));
                                            }}
                                        >
                                            <ArrowRight color="primary" />
                                        </IconButton>
                                    );
                                },
                            },
                        ]}
                    />
                )}
            </TableQuery>
            {tableData && <CSVExportButton exportApi={exportApi} />}
        </ContentWrapper>
    );
};

export default Table;
